/**
 * CandidateMindmap — Phase 4d topic mindmap, modal-first on the candidate
 * profile.
 *
 * Three rings: candidate → 6 fixed categories → topics. No inter-topic edges.
 * Selecting a topic node reveals its citations (round + timestamp + verbatim
 * quote) in the right sidebar — citations are NOT a 4th ring (keeps the
 * canvas readable).
 *
 * Copy discipline (legal-spine carryover): audit/index language only. Topics
 * are nouns naming what was discussed — never traits, tone, or fit. A
 * "[redacted: out-of-scope]" node means the backstop visibly declined a node.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { Loader2, RefreshCw, Sparkles, ShieldAlert, Quote } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";

const STROKE = 1.5;
const COST_HINT = "~$0.03";
const HIDDEN_HANDLE = {
  opacity: 0, left: "50%", top: "50%", transform: "translate(-50%,-50%)",
  width: 6, height: 6, minWidth: 0, minHeight: 0, border: "none",
  background: "transparent", pointerEvents: "none",
};

function CenterHandles() {
  return (
    <>
      <Handle type="target" position={Position.Top} style={HIDDEN_HANDLE} isConnectable={false} />
      <Handle type="source" position={Position.Bottom} style={HIDDEN_HANDLE} isConnectable={false} />
    </>
  );
}

function RootNode({ data }) {
  return (
    <div
      className="flex h-[88px] w-[160px] items-center justify-center rounded-2xl bg-emerald-800 px-4 text-center text-[13px] font-medium leading-tight text-white shadow-lg shadow-emerald-900/20"
      data-testid="mindmap-node-root"
    >
      <CenterHandles />
      {data.name}
    </div>
  );
}

function CategoryNode({ data }) {
  const empty = data.count === 0;
  return (
    <div
      className={`flex w-[168px] flex-col items-center justify-center rounded-xl border px-3 py-2.5 text-center shadow-sm ${
        empty
          ? "border-dashed border-ink-200 bg-ink-50 text-ink-400"
          : "border-emerald-800/25 bg-emerald-50 text-emerald-900"
      }`}
      data-testid={`mindmap-node-category-${data.key}`}
    >
      <CenterHandles />
      <div className="text-[12.5px] font-semibold leading-tight">{data.label}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-ink-500">
        {empty ? "Not discussed" : `${data.count} topic${data.count === 1 ? "" : "s"}`}
      </div>
    </div>
  );
}

function TopicNode({ data }) {
  const { topic, selected } = data;
  if (topic.redacted) {
    return (
      <div
        className={`flex w-[150px] items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11.5px] font-medium shadow-sm transition ${
          selected ? "border-amber-500 ring-2 ring-amber-300" : "border-amber-300 bg-amber-50"
        } text-amber-800`}
        data-testid={data.testid}
        title="The compliance backstop declined this node — view the raw quote in the sidebar"
      >
        <CenterHandles />
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" strokeWidth={STROKE} />
        <span className="leading-tight">Out of scope</span>
      </div>
    );
  }
  return (
    <div
      className={`w-[150px] cursor-pointer rounded-lg border bg-white px-2.5 py-2 text-[11.5px] font-medium leading-tight text-ink-800 shadow-sm transition hover:border-emerald-800/40 ${
        selected ? "border-emerald-800 ring-2 ring-emerald-300" : "border-ink-200"
      }`}
      data-testid={data.testid}
    >
      <CenterHandles />
      {topic.label}
    </div>
  );
}

const NODE_TYPES = { root: RootNode, category: CategoryNode, topic: TopicNode };

const CAT_R = 300;
const TOPIC_R = 560;
const d2r = (d) => (d * Math.PI) / 180;

function buildGraph(categories, candidateName, selectedId) {
  const nodes = [{ id: "root", type: "root", position: { x: 0, y: 0 }, data: { name: candidateName } }];
  const edges = [];
  const topicById = {};
  (categories || []).forEach((cat, i) => {
    const a = -90 + i * 60;
    const cx = CAT_R * Math.cos(d2r(a));
    const cy = CAT_R * Math.sin(d2r(a));
    const catId = `cat-${cat.key}`;
    nodes.push({
      id: catId, type: "category", position: { x: cx, y: cy },
      data: { key: cat.key, label: cat.label, count: cat.topics.length },
    });
    edges.push({
      id: `e-root-${cat.key}`, source: "root", target: catId, type: "straight",
      style: { stroke: "#065F46", strokeWidth: 1.5, opacity: 0.55 },
    });
    const k = cat.topics.length;
    const step = k > 1 ? Math.min(46 / (k - 1), 12) : 0;
    cat.topics.forEach((t, j) => {
      const ta = a + (j - (k - 1) / 2) * step;
      const r = TOPIC_R + (j % 2) * 96;
      const tx = r * Math.cos(d2r(ta));
      const ty = r * Math.sin(d2r(ta));
      const tid = `topic-${cat.key}-${j}`;
      topicById[tid] = { ...t, _category: cat.label };
      nodes.push({
        id: tid, type: "topic", position: { x: tx, y: ty },
        data: { topic: t, selected: selectedId === tid, testid: `mindmap-node-${tid}` },
      });
      edges.push({
        id: `e-${cat.key}-${j}`, source: catId, target: tid, type: "straight",
        style: { stroke: t.redacted ? "#d97706" : "#94a3b8", strokeWidth: 1, opacity: 0.5 },
      });
    });
  });
  return { nodes, edges, topicById };
}

export default function CandidateMindmap({ candidateId, candidateName, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/candidates/${candidateId}/mindmap`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load the topic map"))
      .finally(() => setLoading(false));
  }, [candidateId]);

  // Radix fires onOpenChange only for INTERNAL close triggers (ESC / X /
  // overlay), not when the parent opens the dialog via its button — so load
  // here on `open` becoming true.
  useEffect(() => {
    if (open) { setSelectedId(null); load(); }
  }, [open, load]);

  const handleOpenChange = (v) => {
    if (!v) onClose();
  };

  const generate = () => {
    setGenerating(true);
    setSelectedId(null);
    api.post(`/candidates/${candidateId}/mindmap/generate`)
      .then(({ data }) => { setData(data); toast.success("Topic map updated"); })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 429) toast.error("Daily AI budget reached for this workspace. Resets at 00:00 UTC.");
        else if (status === 400) toast.error(e?.response?.data?.detail || "Nothing to map yet");
        else toast.error("Couldn't build the topic map. Try again.");
      })
      .finally(() => setGenerating(false));
  };

  const { nodes, edges, topicById } = useMemo(
    () => buildGraph(data?.categories, candidateName, selectedId),
    [data, candidateName, selectedId],
  );

  const onNodeClick = useCallback((_e, node) => {
    if (node.type === "topic") setSelectedId((cur) => (cur === node.id ? null : node.id));
    else setSelectedId(null);
  }, []);

  const selectedTopic = selectedId ? topicById[selectedId] : null;
  const hasMap = data?.exists;
  const totalTopics = (data?.categories || []).reduce((s, c) => s + c.topics.length, 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="block h-[90vh] w-[96vw] max-w-[1400px] gap-0 overflow-hidden p-0"
        data-testid="mindmap-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
          <div>
            <DialogTitle className="font-display text-lg font-medium text-ink-900">
              Topic map · {candidateName}
            </DialogTitle>
            <DialogDescription className="text-[12px] text-ink-500">
              {hasMap
                ? `What was discussed across ${data?.round_count ?? 0} round${(data?.round_count) === 1 ? "" : "s"} · grouped into six fixed categories`
                : `${data?.current_round_count ?? 0} transcribed round${(data?.current_round_count) === 1 ? "" : "s"} ready to map · grouped into six fixed categories`}
            </DialogDescription>
          </div>
          {hasMap && (
            <button
              onClick={generate}
              disabled={generating}
              className="mr-8 inline-flex items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-700 hover:border-emerald-800/40 hover:text-emerald-900 disabled:opacity-50"
              data-testid="mindmap-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${generating ? "animate-spin" : ""}`} strokeWidth={STROKE} />
              Refresh ({COST_HINT})
            </button>
          )}
        </div>

        {/* Stale banner */}
        {hasMap && data?.is_stale && (
          <div
            className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-5 py-2 text-[12.5px] text-amber-900"
            data-testid="mindmap-stale-banner"
          >
            <span>
              This map was built for {data.cached_round_count} round{data.cached_round_count === 1 ? "" : "s"} —
              you now have {data.current_round_count}. Refresh to include the latest.
            </span>
            <button
              onClick={generate}
              disabled={generating}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-700 px-3 py-1 text-[12px] font-medium text-white hover:bg-amber-800 disabled:opacity-50"
              data-testid="mindmap-stale-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${generating ? "animate-spin" : ""}`} strokeWidth={STROKE} />
              Refresh ({COST_HINT})
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex h-[calc(90vh-58px)]" style={data?.is_stale && hasMap ? { height: "calc(90vh - 98px)" } : {}}>
          {/* Canvas */}
          <div className="relative flex-1 bg-ink-50/40" data-testid="mindmap-canvas">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} />
              </div>
            )}

            {!loading && !hasMap && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center" data-testid="mindmap-empty-state">
                <Sparkles className="h-7 w-7 text-emerald-800" strokeWidth={STROKE} />
                <p className="mt-3 max-w-sm text-[14px] text-ink-600">
                  Build a topic map of everything this candidate discussed across their rounds — grouped into six fixed categories, every topic backed by a verbatim quote.
                </p>
                <button
                  onClick={generate}
                  disabled={generating}
                  className="mt-4 inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:opacity-60"
                  data-testid="mindmap-generate-btn"
                >
                  {generating
                    ? <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> Mapping topics…</>
                    : <><Sparkles className="h-4 w-4" strokeWidth={STROKE} /> Generate topic map ({COST_HINT})</>}
                </button>
              </div>
            )}

            {generating && hasMap && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-sm" data-testid="mindmap-loading">
                <div className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-[13px] text-ink-700 shadow">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-800" strokeWidth={STROKE} /> Rebuilding topic map…
                </div>
              </div>
            )}

            {hasMap && (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                onNodeClick={onNodeClick}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.2}
                maxZoom={1.5}
                nodesDraggable={false}
                nodesConnectable={false}
                proOptions={{ hideAttribution: true }}
              >
                <Background color="#cbd5e1" gap={22} />
                <Controls showInteractive={false} />
              </ReactFlow>
            )}
          </div>

          {/* Sidebar */}
          <aside className="w-80 shrink-0 overflow-y-auto border-l border-ink-200 bg-white p-4" data-testid="mindmap-sidebar">
            {!selectedTopic && (
              <div className="flex h-full flex-col items-center justify-center text-center text-ink-400">
                <Quote className="h-5 w-5" strokeWidth={STROKE} />
                <p className="mt-2 px-4 text-[12.5px]">
                  {hasMap
                    ? "Select a topic to see exactly what was said, with round and timestamp."
                    : "Generate the map to explore topics and their quotes."}
                </p>
                {hasMap && (
                  <p className="mt-4 text-[11px] text-ink-400">{totalTopics} topics across 6 categories</p>
                )}
              </div>
            )}
            {selectedTopic && (
              <div data-testid="mindmap-citations">
                <div className="text-[10px] uppercase tracking-wider text-ink-500">{selectedTopic._category}</div>
                <h3 className={`mt-1 font-display text-[15px] font-medium ${selectedTopic.redacted ? "text-amber-800" : "text-ink-900"}`}>
                  {selectedTopic.redacted ? "Out-of-scope node (declined)" : selectedTopic.label}
                </h3>
                {selectedTopic.redacted && (
                  <p className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11.5px] text-amber-800">
                    The compliance backstop declined this label as a non-verbal judgment. The verbatim quotes below are what was actually said.
                  </p>
                )}
                <div className="mt-3 space-y-3">
                  {selectedTopic.citations.length === 0 && (
                    <p className="text-[12.5px] text-ink-500">No quote captured for this topic.</p>
                  )}
                  {selectedTopic.citations.map((c, idx) => (
                    <div key={idx} className="rounded-md border border-ink-200 bg-ink-50/60 p-2.5" data-testid={`mindmap-citation-${idx}`}>
                      <div className="mb-1 text-[11px] font-medium text-emerald-800">
                        Round {c.round}{c.timestamp ? ` · ~${c.timestamp}` : ""}
                      </div>
                      <p className="text-[12.5px] italic leading-relaxed text-ink-700">“{c.quote}”</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
